import { STORE_DETAILS } from "@graphql/fragments/store-details";
import { STORE_CATALOG_CACHE } from "@graphql/fragments/store/catalog-cache";
import { gql } from "@stadium/services/apollo-client/server";

export const fetchStoreTopBanners = gql`
  query getStoresTopBanners($type: String!, $accountIds: [Int!]) {
    stores(type: $type, accountIds: $accountIds) {
      id
      name
      data: topBanner
      config {
        type
      }
    }
  }
`;

export const fetchPaginatedStoresQuery = gql`
  query paginatedStores(
    $type: String!
    $accountIds: [Int!]
    $state: String!
    $page: Int
    $perPage: Int
    $search: String
    $excludePriceless: Boolean
    $noCache: Boolean
    $createdByIds: [Int!]
  ) {
    paginatedStores(
      type: $type
      accountIds: $accountIds
      state: $state
      page: $page
      perPage: $perPage
      name: $search
      excludePriceless: $excludePriceless
      noCache: $noCache
      createdByIds: $createdByIds
    ) {
      collection {
        ... on Store {
          active
          createdAt
          id
          name
          topBanner
          feUrl: feUrlWithWorkspaceSlug
          number
          adminsCount
          creator {
            email
            name
            deactivatedInCurrentWorkspace
          }
          currencyType
          permissions
          config {
            currencyName
          }
        }
      }
      total
      metadata {
        all
        incomplete
        live
        archived
      }
    }
  }
`;

export const fetchAccountStoresQuery = gql`
  query getAccountStores($type: String!, $limit: Int, $accountIds: [Int!]) {
    stores(type: $type, limit: $limit, accountIds: $accountIds) {
      config {
        type
      }
      id
      storeName: name
      sampleProductsImage: productImage
      topBannerImage: topBanner
      feUrl: feUrlWithWorkspaceSlug
      storeNumber: number
      code
      account {
        id
        externalId
      }
      storeDetail {
        shortDescription
        longDescription
      }
    }
  }
`;

export const fetchStoreQuery = gql`
  query getStore(
    $number: String
    $authorize: Boolean
    $customDomainRoot: String
    $onlyActive: Boolean
    $isAuthenticated: Boolean = false
    $includeCatalogVisitedOrganizers: Boolean = false
    $dashboardPermissions: Boolean = false
  ) {
    store(
      number: $number
      authorize: $authorize
      customDomainRoot: $customDomainRoot
      onlyActive: $onlyActive
    ) {
      cloneId
      parentId
      syncedToEs
      workingOnFallback
      cloningInProgress
      showLoader
      shopType
      visitedOrganizers
      catalogVisitedOrganizers @include(if: $includeCatalogVisitedOrganizers)
      editCatalogVisitedOrganizers @include(if: $includeCatalogVisitedOrganizers)
      catalogStoreIds
      restrictCategoryWidgets
      parent {
        id
        storeDetail {
          templateType
        }
      }
      editAccessStoreAdmins {
        id
        email
      }
      readAccessStoreAdmins {
        id
        email
      }
      creator {
        email
      }
      ...StoreDetails
      ...StoreCatalogCache
    }
    storeWithPermissions: store(
      number: $number
      authorize: $authorize
      customDomainRoot: $customDomainRoot
      onlyActive: $onlyActive
      noCache: true
    ) {
      id
      permissions(dashboardPermissions: $dashboardPermissions)
    }
    user @include(if: $isAuthenticated) {
      name
      membership {
        subscribed
      }
    }
  }
  ${STORE_DETAILS}
  ${STORE_CATALOG_CACHE}
`;

export const fetchStoreImagesByExternalIds = gql`
  query getStoreImagesByExternalIds($externalIds: String!) {
    stores(externalIds: $externalIds) {
      code: shopCodeWithWorkspaceSlug
      number
      config {
        currencyCode
        currencyName
        currencyPrefix
        currencySuffix
      }
      name
      externalId
      feUrl: feUrlWithWorkspaceSlug
      topBanner
      pointsMigrationAllowed
      productsThumbImage: productImage
      storeDetail {
        thankYouTitle
        thankYouMessage
      }
    }
  }
`;

export const fetchStoreImagesByExternalIdsDashboard = gql`
  query getStoreImagesByExternalIdsDashboard($externalIds: String!) {
    stores(externalIds: $externalIds) {
      number
      externalId
      topBanner
      pointsMigrationAllowed
      config {
        currencyCode
        currencyName
        currencyPrefix
        currencySuffix
      }
      storeDetail {
        thankYouTitle
        thankYouMessage
        hideThankYouMessagePrompts
        hideThankYouMessagePlaceholder
      }
    }
  }
`;

export const fetchDefaultStores = gql`
  query stores($type: String!) {
    stores(type: $type) {
      id
      name
      topBanner
      feUrl: feUrlWithWorkspaceSlug
      storeDetail {
        shortDescription
      }
    }
  }
`;

export const fetchDefaultShopForRecognitionProgram = gql`
  query fetchDefaultShopForRecognitionProgram($type: String!, $page: Int, $perPage: Int) {
    paginatedStores(type: $type, page: $page, perPage: $perPage) {
      collection {
        ... on Store {
          id
          name
          feUrl: feUrlWithWorkspaceSlug
        }
      }
    }
  }
`;

export const fetchShopForRecognitionProgram = gql`
  query getShopForRecognitionProgram($number: String, $authorize: Boolean) {
    store(number: $number, authorize: $authorize) {
      id
      name
      topBanner
      number
      feUrl: feUrlWithWorkspaceSlug
    }
  }
`;

export const fetchCatalogMetaDetail = `
  query fetchCatalogMetaDetail($number: String) {
    catalog(number: $number) {
      id
      name
      creatorDomain
    }
  }
`;

export const fetchCloningProgress = gql`
  query fetchCloningProgress($number: String, $authorize: Boolean, $noCache: Boolean) {
    store(number: $number, authorize: $authorize, noCache: $noCache) {
      parentId
      id
      showLoader
    }
  }
`;

export const fetchMinifiedStoreDetails = gql`
  query getMinifiedStoreDetails($externalIds: String!) {
    stores(externalIds: $externalIds) {
      number
      externalId
      topBanner
      feUrl: feUrlWithWorkspaceSlug
    }
  }
`;
